import React, { useContext, useMemo, memo, useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Card,
  Divider,
  TextField,
  InputAdornment,
  Collapse,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  ChevronRight as ChevronRightIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import { usePageHelpOverride } from "../../utils/components/pageInfo/PageHelpOverrideContext";
import { DataContext } from "../../contexts/DataContext";
import { useTheme } from "../../contexts/ThemeContext";
import {
  getMm,
  getModernMaterialPageSx,
} from "../../theme/modernMaterialEnterprise";
import {
  SETTINGS_PERMISSIONS,
  hasSettingsPermission,
  userHasAttendanceKioskLogin,
  userHasPosKioskLogin,
  userHasBirthdayKioskLogin,
  userCanAccessPosKiosk,
  hasLokStodAccess,
  hasAnmalningarAccess,
  hasGroupMailingsAccess,
} from "../../realm/settingsPermissions";
import { pageContainerSx } from "../../constants/layout";
import { attentionBadgePopSx } from "../../constants/attentionBadgeMotion";
import {
  People as PeopleIcon,
  Groups as GroupsIcon,
  School as SchoolIcon,
  Person as PersonIcon,
  AccountBalance as EconomyIcon,
  TrendingUp as PromotionsIcon,
  HowToReg as ApprovalIcon,
  MilitaryTech as BeltIcon,
  Settings as SettingsIcon,
  BarChart as AttendanceIcon,
  VerifiedUser as LokStodIcon,
  CalendarMonth as ScheduleIcon,
  Article as ContentIcon,
  QrCodeScanner as QrIcon,
  Timer as TimerIcon,
  PointOfSale as PosIcon,
  AppRegistration as AnmalningarIcon,
  MailOutline as MailOutlineIcon,
} from "@mui/icons-material";

const SETTINGS_COLLAPSED_STORAGE_PREFIX = "settingsCollapsedCards";
const SETTINGS_NAV_INDENT = { xs: 2, sm: 2.5 };
const SETTINGS_NAV_SIDE_PAD = { xs: 1.25, sm: 1.5 };

function getSettingsCollapsedStorageKey(clubID) {
  return clubID != null ? `${SETTINGS_COLLAPSED_STORAGE_PREFIX}_${clubID}` : SETTINGS_COLLAPSED_STORAGE_PREFIX;
}

function readSettingsCollapsedCards(clubID) {
  try {
    const raw = localStorage.getItem(getSettingsCollapsedStorageKey(clubID));
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeSettingsCollapsedCards(clubID, state) {
  try {
    localStorage.setItem(getSettingsCollapsedStorageKey(clubID), JSON.stringify(state));
  } catch {
    /* ignore */
  }
}

function SettingsCollapseButton({ collapsed, onToggle, collapseLabel, expandLabel, isDarkMode, onClick }) {
  const MM = getMm(isDarkMode);
  const handleClick = (event) => {
    event.stopPropagation();
    onClick?.(event);
    onToggle?.();
  };

  return (
    <Tooltip title={collapsed ? expandLabel : collapseLabel} arrow>
      <IconButton
        size="small"
        onClick={handleClick}
        aria-expanded={!collapsed}
        aria-label={collapsed ? expandLabel : collapseLabel}
        sx={{ color: MM.onSurfaceVariant, flexShrink: 0 }}
      >
        {collapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
      </IconButton>
    </Tooltip>
  );
}

const SECTION_ACCENTS = {
  settings_category_members: { light: "#DBEAFE", dark: "rgba(59, 130, 246, 0.18)", icon: "#2563EB" },
  settings_category_club_setup: { light: "#E5E7EB", dark: "rgba(156, 163, 175, 0.18)", icon: "#4B5563" },
  settings_category_economy: { light: "#D1FAE5", dark: "rgba(16, 185, 129, 0.18)", icon: "#059669" },
  settings_category_grading: { light: "#FEF3C7", dark: "rgba(245, 158, 11, 0.18)", icon: "#D97706" },
  settings_category_staff: { light: "#EDE9FE", dark: "rgba(139, 92, 246, 0.18)", icon: "#7C3AED" },
  settings_category_attendance: { light: "#FEE2E2", dark: "rgba(239, 68, 68, 0.16)", icon: "#DC2626" },
  settings_category_attendance_kiosks: { light: "#FEE2E2", dark: "rgba(239, 68, 68, 0.16)", icon: "#DC2626" },
  settings_category_billing: { light: "#D1FAE5", dark: "rgba(16, 185, 129, 0.18)", icon: "#059669" },
  settings_category_content_tools: { light: "#E0F2FE", dark: "rgba(14, 165, 233, 0.18)", icon: "#0284C7" },
  settings_category_instruction: { light: "#EEEEEE", dark: "rgba(28, 27, 31, 0.08)", icon: "#1C1B1F" },
};

const userAdminSurfaceSx = (isDarkMode) => {
  const MM = getMm(isDarkMode);
  return {
    backgroundColor: MM.surfaceLowest,
    border: `1px solid ${MM.outlineVariant}`,
    borderRadius: MM.radiusMd,
    boxShadow: MM.elev1,
    fontFamily: MM.fontBody,
  };
};

const settingsCardHeaderSx = (isDarkMode) => {
  const MM = getMm(isDarkMode);
  return {
    backgroundColor: MM.surfaceLow,
    borderBottom: `1px solid ${MM.outlineVariant}`,
  };
};

function SettingsNavRowContent({ item, badgeCount, isDarkMode, t, showDescription, suppressClearBadge }) {
  const MM = getMm(isDarkMode);
  const Icon = item.icon;
  const shouldShowDescription = showDescription ?? item.showDescription !== false;

  return (
    <>
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: "10px",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: 'rgba(28, 27, 31, 0.05)',
        }}
      >
        <Icon
          sx={{
            fontSize: 20,
            color: MM.onSurfaceVariant,
          }}
        />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, pr: item.badgeKey && badgeCount > 0 ? 1 : 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap", minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "0.875rem",
              color: MM.onSurface,
              lineHeight: 1.3,
              fontFamily: MM.fontBody,
              minWidth: 0,
              overflowWrap: "break-word",
              wordBreak: "break-word",
            }}
          >
            {t(item.labelKey)}
          </Typography>
          {item.configBadge && (
            <Typography
              component="span"
              sx={{
                fontSize: "0.625rem",
                fontWeight: 700,
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                color: MM.onSurfaceVariant,
                fontFamily: MM.fontBody,
                px: 0.75,
                py: 0.15,
                borderRadius: "4px",
                backgroundColor: 'rgba(28, 27, 31, 0.05)',
              }}
            >
              {t("settings_config_badge")}
            </Typography>
          )}
          {item.unavailableChipKey && (
            <AttentionChip label={t(item.unavailableChipKey)} isDarkMode={isDarkMode} />
          )}
        </Box>
        {shouldShowDescription && item.descKey && (
          <Typography
            sx={{
              mt: 0.25,
              fontSize: "0.75rem",
              fontWeight: 500,
              color: MM.onSurfaceVariant,
              lineHeight: 1.35,
              fontFamily: MM.fontBody,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              overflowWrap: "break-word",
            }}
          >
            {t(item.descKey)}
          </Typography>
        )}
        {item.showClearBadge && item.badgeKey && badgeCount === 0 && !suppressClearBadge && (
          <Typography
            sx={{
              display: { xs: "block", sm: "none" },
              mt: 0.5,
              fontSize: "0.6875rem",
              fontWeight: 700,
              letterSpacing: "0.02em",
              textTransform: "uppercase",
              color: MM.secondary,
              fontFamily: MM.fontBody,
              lineHeight: 1.3,
              overflowWrap: "break-word",
            }}
          >
            {t("settings_attention_all_clear")}
          </Typography>
        )}
      </Box>
      {item.badgeKey && badgeCount > 0 && (
        <SettingsAttentionCountBadge count={badgeCount} isDarkMode={isDarkMode} />
      )}
      {item.showClearBadge && item.badgeKey && badgeCount === 0 && !suppressClearBadge && (
        <Typography
          component="span"
          sx={{
            display: { xs: "none", sm: "inline" },
            fontSize: "0.625rem",
            fontWeight: 700,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            color: MM.secondary,
            fontFamily: MM.fontBody,
            flexShrink: 1,
            minWidth: 0,
            maxWidth: 148,
            overflowWrap: "break-word",
            wordBreak: "break-word",
            lineHeight: 1.25,
            textAlign: "right",
          }}
        >
          {t("settings_attention_all_clear")}
        </Typography>
      )}
      <ChevronRightIcon
        sx={{
          fontSize: 18,
          flexShrink: 0,
          color: MM.onSurfaceVariant,
          opacity: 0.85,
        }}
      />
    </>
  );
}

function SettingsAttentionCountBadge({ count, isDarkMode }) {
  if (!count || count <= 0) return null;
  const MM = getMm(isDarkMode);
  return (
    <Box
      component="span"
      sx={{
        minWidth: 22,
        height: 22,
        px: 0.625,
        borderRadius: "11px",
        backgroundColor: "#DC2626",
        color: "#FFFFFF",
        fontSize: "0.6875rem",
        fontWeight: 700,
        lineHeight: "22px",
        textAlign: "center",
        flexShrink: 0,
        fontFamily: MM.fontBody,
        ...attentionBadgePopSx,
      }}
    >
      {count}
    </Box>
  );
}

function SettingsSearchBar({ value, onChange, isDarkMode, t }) {
  const MM = getMm(isDarkMode);
  return (
    <TextField
      fullWidth
      size="small"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={t("settings_search_placeholder", "Search settings…")}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon sx={{ fontSize: 20, color: MM.onSurfaceVariant }} />
          </InputAdornment>
        ),
      }}
      sx={{
        mb: { xs: 1.5, sm: 2 },
        fontFamily: MM.fontBody,
        "& .MuiOutlinedInput-root": {
          borderRadius: "10px",
          backgroundColor: MM.surfaceLowest,
          fontFamily: MM.fontBody,
          "& fieldset": {
            borderColor: MM.outlineVariant,
            borderWidth: { xs: 1, sm: 2 },
          },
          "&:hover fieldset": {
            borderColor: MM.outline,
          },
          "&.Mui-focused fieldset": {
            borderColor: MM.primary,
            borderWidth: "2px",
          },
        },
        "& .MuiInputBase-input": {
          color: MM.onSurface,
          fontFamily: MM.fontBody,
          fontSize: "0.875rem",
          py: 1.25,
        },
      }}
    />
  );
}

function SettingsSectionPanel({
  section,
  onNavigate,
  getBadgeCount,
  isDarkMode,
  t,
  collapsed,
  isCollapsible,
  onToggleCollapse,
  collapseLabel,
  expandLabel,
  attentionWhenBadgeItemIds,
  suppressClearBadge,
}) {
  const MM = getMm(isDarkMode);
  const SectionIcon = section.icon;
  const accent = SECTION_ACCENTS[section.labelKey] || SECTION_ACCENTS.settings_category_club_setup;

  const sectionAttentionCount = section.items.reduce((sum, item) => {
    if (!item.badgeKey) return sum;
    return sum + (getBadgeCount(item.badgeKey) || 0);
  }, 0);

  return (
    <Card
      elevation={0}
      sx={{
        ...userAdminSurfaceSx(isDarkMode),
        overflow: "hidden",
        height: collapsed ? "auto" : "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1.25,
          px: { xs: 1.5, sm: 1.75 },
          py: { xs: 1.25, sm: 1.5 },
          ...settingsCardHeaderSx(isDarkMode),
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "10px",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: accent.light,
          }}
        >
          <SectionIcon sx={{ fontSize: 22, color: accent.icon }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap", minWidth: 0 }}>
            <Typography
              sx={{
                fontWeight: { xs: 800, sm: 700 },
                fontSize: { xs: "1.0625rem", sm: "1rem" },
                color: MM.onSurface,
                fontFamily: MM.fontBody,
                lineHeight: 1.25,
                letterSpacing: { xs: "-0.01em", sm: 0 },
                minWidth: 0,
                overflowWrap: "break-word",
              }}
            >
              {t(section.labelKey)}
            </Typography>
            <Typography
              component="span"
              sx={{
                fontSize: "0.6875rem",
                fontWeight: 700,
                color: MM.onSurfaceVariant,
                fontFamily: MM.fontBody,
                px: 0.625,
                py: 0.125,
                borderRadius: "999px",
                backgroundColor: 'rgba(28, 27, 31, 0.06)',
              }}
            >
              {section.items.length}
            </Typography>
            {collapsed && sectionAttentionCount > 0 ? (
              <SettingsAttentionCountBadge count={sectionAttentionCount} isDarkMode={isDarkMode} />
            ) : null}
          </Box>
          {!collapsed && section.descKey && (
            <Typography
              sx={{
                mt: 0.25,
                fontSize: "0.8125rem",
                fontWeight: 500,
                color: MM.onSurfaceVariant,
                lineHeight: 1.4,
                fontFamily: MM.fontBody,
              }}
            >
              {t(section.descKey)}
            </Typography>
          )}
        </Box>
        {isCollapsible && (
          <SettingsCollapseButton
            collapsed={collapsed}
            onToggle={() => onToggleCollapse?.(section.labelKey)}
            collapseLabel={collapseLabel}
            expandLabel={expandLabel}
            isDarkMode={isDarkMode}
          />
        )}
      </Box>

      <Collapse in={!collapsed} timeout="auto" unmountOnExit>
        <Box
          sx={{
            pl: SETTINGS_NAV_INDENT,
            pr: SETTINGS_NAV_SIDE_PAD,
            pb: { xs: 1, sm: 1.25 },
          }}
        >
          {section.items.map((item, index) => {
            const badgeCount = getBadgeCount(item.badgeKey);
            const attentionRow =
              Boolean(item.badgeKey) &&
              badgeCount > 0 &&
              (attentionWhenBadgeItemIds || []).includes(item.id);
            return (
              <Box key={item.id}>
                <Box
                  onClick={() => onNavigate(item)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") onNavigate(item);
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: { xs: 1, sm: 1.25 },
                    pl: { xs: 1, sm: 1.25 },
                    pr: { xs: 0.75, sm: 1.25 },
                    py: 1.35,
                    minHeight: 68,
                    minWidth: 0,
                    overflow: "hidden",
                    cursor: "pointer",
                    borderRadius: "8px",
                    transition: "background-color 0.18s ease, border-color 0.18s ease",
                    ...(attentionRow && {
                      borderLeft: `3px solid #D97706`,
                      pl: { xs: 0.875, sm: 1.125 },
                      backgroundColor: "#FFFBEB",
                    }),
                    "&:hover": {
                      backgroundColor: attentionRow ? "#FEF3C7" : MM.surfaceLow,
                    },
                    "&:active": {
                      backgroundColor: MM.surfaceContainer,
                    },
                  }}
                >
                  <SettingsNavRowContent
                    item={item}
                    badgeCount={badgeCount}
                    isDarkMode={isDarkMode}
                    t={t}
                    suppressClearBadge={suppressClearBadge}
                  />
                </Box>
                {index < section.items.length - 1 && (
                  <Divider
                    sx={{
                      borderColor: MM.outlineVariant,
                      ml: { xs: "3.75rem", sm: "4.25rem" },
                      mr: { xs: 0.5, sm: 0.75 },
                    }}
                  />
                )}
              </Box>
            );
          })}
        </Box>
      </Collapse>
    </Card>
  );
}

const CLUB_SETTINGS_ENTRY = {
  id: "club-settings",
  permission: SETTINGS_PERMISSIONS.CLUB_SETTINGS,
  page: "ClubSettingsPage",
  labelKey: "club_settings",
  descKey: "settings_card_club_settings_desc",
  icon: SettingsIcon,
  configBadge: true,
};

const APPROVAL_CENTER_ENTRY = {
  id: "approval-center",
  permission: SETTINGS_PERMISSIONS.APPROVAL_CENTER,
  page: "ApprovalCenterPage",
  labelKey: "approval_center",
  descKey: "settings_approval_center_desc",
  icon: ApprovalIcon,
  badgeKey: "approvalCenter",
  showClearBadge: true,
};

const CLUB_SETUP_SECTION_META = {
  labelKey: "settings_category_club_setup",
  descKey: "settings_category_club_setup_desc",
  icon: SettingsIcon,
};

function AnmalningarAttentionBanner({
  isDarkMode,
  t,
  count,
  onOpen,
  collapsed,
  isCollapsible,
  onToggleCollapse,
  collapseLabel,
  expandLabel,
}) {
  const MM = getMm(isDarkMode);
  if (count <= 0) return null;

  return (
    <Card
      onClick={collapsed ? undefined : onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (collapsed) return;
        if (e.key === "Enter" || e.key === " ") onOpen();
      }}
      elevation={0}
      sx={{
        ...userAdminSurfaceSx(isDarkMode),
        mb: { xs: 1.5, sm: 2 },
        cursor: "pointer",
        borderColor: MM.outlineVariant,
        "&:hover": {
          backgroundColor: MM.surfaceLow,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: { xs: 1.5, sm: 2 },
          py: 1.25,
          ...settingsCardHeaderSx(isDarkMode),
        }}
      >
        <AnmalningarIcon sx={{ fontSize: 22, color: MM.primary }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "0.875rem",
              color: MM.onSurface,
              fontFamily: MM.fontBody,
            }}
          >
            {t("settings_attention_anmalningar", { count })}
          </Typography>
        </Box>
        {collapsed ? <SettingsAttentionCountBadge count={count} isDarkMode={isDarkMode} /> : null}
        {isCollapsible && (
          <SettingsCollapseButton
            collapsed={collapsed}
            onToggle={() => onToggleCollapse?.("anmalningar-banner")}
            collapseLabel={collapseLabel}
            expandLabel={expandLabel}
            isDarkMode={isDarkMode}
          />
        )}
        {!collapsed && (
          <ChevronRightIcon sx={{ fontSize: 20, color: MM.onSurfaceVariant }} />
        )}
      </Box>
    </Card>
  );
}

const SETTINGS_SECTIONS = [
  {
    labelKey: "settings_category_members",
    descKey: "settings_category_members_desc",
    icon: PeopleIcon,
    items: [
      {
        id: "user-admin",
        permission: SETTINGS_PERMISSIONS.USER_ADMIN,
        page: "UserAdminPage",
        labelKey: "user_admin",
        descKey: "settings_card_user_admin_desc",
        icon: PeopleIcon,
      },
      {
        id: "training-groups",
        permission: SETTINGS_PERMISSIONS.GROUPS,
        page: "TrainingGroupsPage",
        labelKey: "settings_training_groups",
        descKey: "settings_training_groups_desc",
        icon: GroupsIcon,
      },
      {
        id: "group-mailings",
        permission: SETTINGS_PERMISSIONS.GROUP_MAILINGS,
        page: "GroupMailingsPage",
        labelKey: "settings_group_mailings",
        descKey: "settings_group_mailings_desc",
        icon: MailOutlineIcon,
      },
      {
        id: "anmalningar",
        permission: SETTINGS_PERMISSIONS.ANMALNINGAR,
        page: "AnmalningarPage",
        labelKey: "settings_anmalningar_config_title",
        descKey: "settings_card_anmalningar_config_desc",
        icon: AnmalningarIcon,
      },
    ],
  },
  {
    labelKey: "settings_category_billing",
    descKey: "settings_category_billing_desc",
    icon: EconomyIcon,
    items: [
      {
        id: "economy",
        permission: SETTINGS_PERMISSIONS.PAYMENTS,
        page: "FinancePage",
        labelKey: "economy",
        descKey: "settings_card_economy_desc",
        icon: EconomyIcon,
        financeTab: 0,
      },
      {
        id: "pos",
        permission: SETTINGS_PERMISSIONS.POS,
        page: "PosAdminPage",
        labelKey: "pos_admin_title",
        descKey: "settings_card_pos_desc",
        icon: PosIcon,
      },
    ],
  },
  {
    labelKey: "settings_category_grading",
    descKey: "settings_category_grading_desc",
    icon: BeltIcon,
    items: [
      {
        id: "create-promotions",
        permission: SETTINGS_PERMISSIONS.PROMOTIONS,
        page: "BeltPromotionPage",
        labelKey: "settings_create_promotions",
        descKey: "settings_card_promotions_desc",
        icon: PromotionsIcon,
        badgeKey: "promotions",
      },
      {
        id: "belt-records",
        permission: SETTINGS_PERMISSIONS.BELT_ADMIN,
        page: "BeltAdminPage",
        labelKey: "settings_belt_records",
        descKey: "settings_belt_records_desc",
        icon: BeltIcon,
      },
    ],
  },
  {
    labelKey: "settings_category_instruction",
    descKey: "settings_category_instruction_desc",
    icon: SchoolIcon,
    items: [
      {
        id: "coach-management",
        permission: SETTINGS_PERMISSIONS.ADMIN_MENTOR_PAGE,
        page: "MentorManagerPage",
        labelKey: "admin_mentors",
        descKey: "settings_card_admin_mentors_desc",
        icon: SchoolIcon,
      },
      {
        id: "schedule-hub",
        permission: SETTINGS_PERMISSIONS.SCHEDULE,
        page: "ScheduleHubPage",
        labelKey: "settings_schedule_hub_title",
        descKey: "settings_schedule_hub_nav_desc",
        icon: ScheduleIcon,
      },
      {
        id: "mentor-workspace",
        permission: SETTINGS_PERMISSIONS.MENTOR_PAGE,
        page: "MentorListPage",
        labelKey: "mentor_page",
        descKey: "settings_card_mentor_page_desc",
        icon: PersonIcon,
      },
      {
        id: "training-timer",
        permission: SETTINGS_PERMISSIONS.TIMER,
        page: "TimerPage",
        labelKey: "settings_training_timer",
        descKey: "settings_card_timer_desc",
        icon: TimerIcon,
        coachFallback: true,
      },
    ],
  },
  {
    labelKey: "settings_category_attendance_kiosks",
    descKey: "settings_category_attendance_kiosks_desc",
    icon: AttendanceIcon,
    items: [
      {
        id: "qr-checkin",
        permission: SETTINGS_PERMISSIONS.QR_SCANNER,
        page: "QRScannerPage",
        labelKey: "settings_qr_checkin",
        descKey: "settings_card_qr_scanner_desc",
        icon: QrIcon,
      },
      {
        id: "attendance-reports",
        permission: SETTINGS_PERMISSIONS.CLUB_ATTENDANCE,
        page: "ClubAttendanceStats",
        labelKey: "attendance_stats",
        descKey: "settings_card_club_attendance_desc",
        icon: AttendanceIcon,
      },
      {
        id: "lok-stod",
        permission: SETTINGS_PERMISSIONS.LOK_STOD,
        page: "LokStodPage",
        labelKey: "lok_page_title",
        descKey: "settings_card_lok_stod_desc",
        icon: LokStodIcon,
        unavailableChipKey: "settings_lok_not_available_yet",
      },
      {
        id: "birthday-kiosk",
        permission: SETTINGS_PERMISSIONS.BIRTHDAY_KIOSK,
        page: "BirthdayDisplayPage",
        labelKey: "settings_permission_birthday_kiosk",
        descKey: "settings_birthday_kiosk_desc",
        icon: AttendanceIcon,
      },
      {
        id: "pos-kiosk",
        permission: SETTINGS_PERMISSIONS.POS_KIOSK,
        page: "PosShopKioskPage",
        labelKey: "pos_kiosk_title",
        descKey: "settings_card_pos_kiosk_desc",
        icon: PosIcon,
      },
    ],
  },
  {
    labelKey: "settings_category_content_tools",
    descKey: "settings_category_content_desc",
    icon: ContentIcon,
    items: [
      {
        id: "news-content",
        permission: SETTINGS_PERMISSIONS.CONTENT,
        page: "ContentPage",
        labelKey: "settings_news_content",
        descKey: "settings_card_content_desc",
        icon: ContentIcon,
      },
      {
        id: "training-program",
        permission: SETTINGS_PERMISSIONS.TRAINING_PROGRAM,
        page: "TrainingProgramContentPage",
        labelKey: "training_program",
        descKey: "settings_card_training_program_desc",
        icon: ContentIcon,
      },
      {
        id: "video-library",
        permission: SETTINGS_PERMISSIONS.CONTENT,
        page: "VideoLibraryContentPage",
        labelKey: "video_library_title",
        descKey: "settings_card_video_library_desc",
        icon: ContentIcon,
      },
    ],
  },
];

function AttentionChip({ label, isDarkMode, highlight = false }) {
  const MM = getMm(isDarkMode);
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        px: { xs: 1, sm: 1.25 },
        py: { xs: 0.625, sm: 0.5 },
        borderRadius: "8px",
        fontSize: { xs: "0.6875rem", sm: "0.75rem" },
        fontWeight: 600,
        fontFamily: MM.fontBody,
        lineHeight: 1.35,
        color: highlight ? "#B45309" : MM.onSurface,
        background: highlight ? "#FEF3C7" : MM.surfaceLow,
        border: highlight ? "1px solid #FCD34D" : `1px solid ${MM.outlineVariant}`,
        backdropFilter: highlight ? "none" : "blur(8px)",
        WebkitBackdropFilter: highlight ? "none" : "blur(8px)",
        whiteSpace: { xs: "normal", sm: "nowrap" },
        wordBreak: { xs: "break-word", sm: "normal" },
      }}
    >
      {highlight && (
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            flexShrink: 0,
            backgroundColor: "#EF4444",
          }}
        />
      )}
      {label}
    </Box>
  );
}

const SettingsPage = memo(({ selectedPage, setSelectedPage, userInfo, setIsLoading: setGlobalLoading, setFinanceTargetTab }) => {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const MM = getMm(isDarkMode);
  const mmSx = getModernMaterialPageSx(isDarkMode);
  const [searchQuery, setSearchQuery] = useState("");
  const clubID = userInfo?.[0]?.clubID ?? userInfo?.[0]?.clubId;
  const isAdmin = userInfo?.[0]?.isAdmin === true;
  const [collapsedCards, setCollapsedCards] = useState(() => readSettingsCollapsedCards(clubID));
  const {
    adultPromotions,
    childPromotions,
    unpublishedBeltPromotions,
    unreviewedMembersCount,
    pendingParentRequestsCount,
    pendingInstructorAttendancesCount,
    pendingAnmalningSubmissionsCount,
    isLoading,
  } = useContext(DataContext);

  useEffect(() => {
    if (setGlobalLoading && selectedPage === "SettingsPage") setGlobalLoading(isLoading);
  }, [isLoading, selectedPage, setGlobalLoading]);


  useEffect(() => {
    setCollapsedCards(readSettingsCollapsedCards(clubID));
  }, [clubID]);

  const collapseLabel = t("settings_collapse_card");
  const expandLabel = t("settings_expand_card");
  const isCollapsible = isAdmin;

  const toggleCardCollapse = useCallback(
    (cardId) => {
      setCollapsedCards((prev) => {
        const next = { ...prev, [cardId]: !prev[cardId] };
        writeSettingsCollapsedCards(clubID, next);
        return next;
      });
    },
    [clubID]
  );

  const isCardCollapsed = useCallback(
    (cardId) => isCollapsible && Boolean(collapsedCards[cardId]),
    [collapsedCards, isCollapsible]
  );

  const sharedCollapseProps = {
    isCollapsible,
    onToggleCollapse: toggleCardCollapse,
    collapseLabel,
    expandLabel,
  };

  const isCoach = useMemo(() => userInfo?.[0]?.isCoach || false, [userInfo]);

  const gradingPendingCount = useMemo(
    () => adultPromotions + childPromotions,
    [adultPromotions, childPromotions]
  );

  const totalApprovalCenterItems = useMemo(
    () =>
      unreviewedMembersCount +
      pendingParentRequestsCount +
      pendingInstructorAttendancesCount +
      adultPromotions +
      childPromotions +
      pendingAnmalningSubmissionsCount,
    [
      unreviewedMembersCount,
      pendingParentRequestsCount,
      pendingInstructorAttendancesCount,
      adultPromotions,
      childPromotions,
      pendingAnmalningSubmissionsCount,
    ]
  );

  const canOpenApprovalCenter = hasSettingsPermission(userInfo, SETTINGS_PERMISSIONS.APPROVAL_CENTER);
  const canOpenClubSettings = hasSettingsPermission(userInfo, SETTINGS_PERMISSIONS.CLUB_SETTINGS);
  const canOpenAnmalningar = hasAnmalningarAccess(userInfo);
  const anmalningarAttentionCount =
    canOpenAnmalningar && !canOpenApprovalCenter ? pendingAnmalningSubmissionsCount : 0;

  const getBadgeCount = (badgeKey) => {
    if (isLoading) return 0;
    if (badgeKey === "approvalCenter") return totalApprovalCenterItems;
    if (badgeKey === "promotions") return unpublishedBeltPromotions;
    return 0;
  };

  const visibleSections = useMemo(() => {
    const show = (item) => {
      if (item.coachFallback && isCoach) return true;
      if (item.page === "QRScannerPage" && userHasAttendanceKioskLogin(userInfo)) return true;
      if (item.page === "BirthdayDisplayPage" && userHasBirthdayKioskLogin(userInfo)) return true;
      if (item.id === "pos") {
        return (
          hasSettingsPermission(userInfo, SETTINGS_PERMISSIONS.POS) ||
          hasSettingsPermission(userInfo, SETTINGS_PERMISSIONS.PAYMENTS)
        );
      }
      if (item.id === "pos-kiosk") {
        return userCanAccessPosKiosk(userInfo);
      }
      if (item.id === "lok-stod") {
        return hasLokStodAccess(userInfo);
      }
      if (item.id === "anmalningar") {
        return hasAnmalningarAccess(userInfo);
      }
      if (item.id === "group-mailings") {
        return hasGroupMailingsAccess(userInfo);
      }
      if (item.id === "schedule-hub") {
        return (
          hasSettingsPermission(userInfo, SETTINGS_PERMISSIONS.SCHEDULE) ||
          hasSettingsPermission(userInfo, SETTINGS_PERMISSIONS.INSTRUCTOR_SCHEDULE)
        );
      }
      return hasSettingsPermission(userInfo, item.permission);
    };
    return SETTINGS_SECTIONS.map((section) => ({
      ...section,
      items: section.items.filter(show),
    })).filter((section) => section.items.length > 0);
  }, [userInfo, isCoach]);

  const displayedSections = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return visibleSections;

    return visibleSections
      .map((section) => ({
        ...section,
        items: section.items.filter((item) => {
          const title = t(item.labelKey).toLowerCase();
          const desc = item.descKey ? t(item.descKey).toLowerCase() : "";
          const category = t(section.labelKey).toLowerCase();
          return title.includes(query) || desc.includes(query) || category.includes(query);
        }),
      }))
      .filter((section) => section.items.length > 0);
  }, [visibleSections, searchQuery, t]);

  const approvalCenterMatchesSearch = useMemo(() => {
    if (!canOpenApprovalCenter) return false;
    const query = searchQuery.trim().toLowerCase();
    if (!query) return true;
    return (
      t(APPROVAL_CENTER_ENTRY.labelKey).toLowerCase().includes(query) ||
      t(APPROVAL_CENTER_ENTRY.descKey).toLowerCase().includes(query)
    );
  }, [canOpenApprovalCenter, searchQuery, t]);

  const clubSettingsMatchesSearch = useMemo(() => {
    if (!canOpenClubSettings) return false;
    const query = searchQuery.trim().toLowerCase();
    if (!query) return true;
    return (
      t(CLUB_SETTINGS_ENTRY.labelKey).toLowerCase().includes(query) ||
      t(CLUB_SETTINGS_ENTRY.descKey).toLowerCase().includes(query)
    );
  }, [canOpenClubSettings, searchQuery, t]);

  const clubSetupCombinedSection = useMemo(() => {
    const items = [];
    if (canOpenClubSettings && clubSettingsMatchesSearch) items.push(CLUB_SETTINGS_ENTRY);
    if (canOpenApprovalCenter && approvalCenterMatchesSearch) items.push(APPROVAL_CENTER_ENTRY);
    if (items.length === 0) return null;
    return { ...CLUB_SETUP_SECTION_META, items };
  }, [
    canOpenClubSettings,
    clubSettingsMatchesSearch,
    canOpenApprovalCenter,
    approvalCenterMatchesSearch,
  ]);

  const gridSections = displayedSections;

  const hasVisibleResults = Boolean(clubSetupCombinedSection) || gridSections.length > 0;

  /** (i) dialog rows — same icons, titles, and descriptions as the visible settings cards */
  const settingsHelpFeatures = useMemo(() => {
    const iconSx = { color: MM.onSurfaceVariant, fontSize: "1.25rem" };
    const features = [];
    if (canOpenApprovalCenter) {
      features.push({
        icon: <ApprovalIcon sx={iconSx} />,
        title: APPROVAL_CENTER_ENTRY.labelKey,
        description: APPROVAL_CENTER_ENTRY.descKey,
      });
    }
    if (canOpenClubSettings) {
      features.push({
        icon: <SettingsIcon sx={iconSx} />,
        title: CLUB_SETTINGS_ENTRY.labelKey,
        description: CLUB_SETTINGS_ENTRY.descKey,
      });
    }
    visibleSections.forEach((section) => {
      const SectionIcon = section.icon || SettingsIcon;
      features.push({
        icon: <SectionIcon sx={iconSx} />,
        title: section.labelKey,
        description: section.descKey,
      });
      section.items.forEach((item) => {
        const Icon = item.icon;
        features.push({
          icon: <Icon sx={iconSx} />,
          title: item.labelKey,
          description: item.descKey,
        });
      });
    });
    return features;
  }, [visibleSections, isDarkMode, canOpenApprovalCenter, canOpenClubSettings]);

  usePageHelpOverride(
    useMemo(
      () =>
        selectedPage === "SettingsPage"
          ? {
              features: settingsHelpFeatures,
              title: "settings_help_title",
              overview: "settings_overview",
              importantNote: "settings_help_badge_note",
            }
          : null,
      [selectedPage, settingsHelpFeatures]
    )
  );

  const handleNavigate = (item) => {
    if (item.financeTab != null && setFinanceTargetTab) setFinanceTargetTab(item.financeTab);
    if (item.approvalFilter) {
      sessionStorage.setItem("approvalCenterInitialFilter", item.approvalFilter);
    }
    if (item.page === "ApprovalCenterPage") {
      sessionStorage.removeItem("approvalCenterInitialFilter");
    }
    setSelectedPage(item.page);
  };

  const openAnmalningarInbox = () => {
    if (canOpenApprovalCenter) {
      sessionStorage.setItem("approvalCenterInitialFilter", "anmalningar");
      setSelectedPage("ApprovalCenterPage");
      return;
    }
    sessionStorage.setItem("anmalningarInitialTab", "4");
    setSelectedPage("AnmalningarPage");
  };

  if (selectedPage !== "SettingsPage") return null;

  return (
    <Box
      sx={{
        ...pageContainerSx,
        ...mmSx.page,
        display: "flex",
        flexDirection: "column",
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        overflowX: "hidden",
        px: { xs: 1, sm: 2, md: 3 },
        pt: { xs: 1.5, sm: 2.5 },
        pb: { xs: 5, sm: 6 },
        minHeight: "100vh",
        boxSizing: "border-box",
      }}
    >
      <Box sx={{ textAlign: "center", mb: { xs: 1.5, sm: 2 } }}>
        <Typography sx={{ ...mmSx.title, textAlign: "center" }}>
          {t("admin_settings")}
        </Typography>
        {userInfo?.[0]?.clubname && (
          <Typography sx={{ ...mmSx.subtitle, textAlign: "center", mt: 0.5 }}>
            {userInfo[0].clubname}
          </Typography>
        )}
      </Box>

      {canOpenAnmalningar && (
        <AnmalningarAttentionBanner
          isDarkMode={isDarkMode}
          t={t}
          count={anmalningarAttentionCount}
          onOpen={openAnmalningarInbox}
          collapsed={isCardCollapsed("anmalningar-banner")}
          {...sharedCollapseProps}
        />
      )}

      <SettingsSearchBar
        value={searchQuery}
        onChange={setSearchQuery}
        isDarkMode={isDarkMode}
        t={t}
      />

      {hasVisibleResults ? (
        <>
          {(clubSetupCombinedSection || gridSections.length > 0) && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", lg: "repeat(2, 1fr)" },
                gap: { xs: 2, sm: 2.5 },
                alignItems: "start",
              }}
            >
              {(() => {
                const elements = [];
                let clubSetupInserted = false;

                gridSections.forEach((section, index) => {
                  elements.push(
                    <SettingsSectionPanel
                      key={section.labelKey}
                      section={section}
                      onNavigate={handleNavigate}
                      getBadgeCount={getBadgeCount}
                      isDarkMode={isDarkMode}
                      t={t}
                      collapsed={isCardCollapsed(section.labelKey)}
                      {...sharedCollapseProps}
                    />
                  );
                  if (clubSetupCombinedSection && index === 0) {
                    elements.push(
                      <SettingsSectionPanel
                        key="club-setup-combined"
                        section={clubSetupCombinedSection}
                        onNavigate={handleNavigate}
                        getBadgeCount={getBadgeCount}
                        isDarkMode={isDarkMode}
                        t={t}
                        collapsed={isCardCollapsed("settings_category_club_setup")}
                        attentionWhenBadgeItemIds={["approval-center"]}
                        suppressClearBadge={isLoading}
                        {...sharedCollapseProps}
                      />
                    );
                    clubSetupInserted = true;
                  }
                });

                if (clubSetupCombinedSection && !clubSetupInserted) {
                  elements.push(
                    <SettingsSectionPanel
                      key="club-setup-combined"
                      section={clubSetupCombinedSection}
                      onNavigate={handleNavigate}
                      getBadgeCount={getBadgeCount}
                      isDarkMode={isDarkMode}
                      t={t}
                      collapsed={isCardCollapsed("settings_category_club_setup")}
                      attentionWhenBadgeItemIds={["approval-center"]}
                      suppressClearBadge={isLoading}
                      {...sharedCollapseProps}
                    />
                  );
                }

                return elements;
              })()}
            </Box>
          )}
        </>
      ) : (
        <Card
          elevation={0}
          sx={{
            ...userAdminSurfaceSx(isDarkMode),
            px: 2,
            py: 3,
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: "0.9375rem",
              fontWeight: 600,
              color: MM.onSurface,
              fontFamily: MM.fontBody,
              mb: 0.5,
            }}
          >
            {t("settings_no_results", "No settings match your search")}
          </Typography>
          <Typography
            sx={{
              fontSize: "0.8125rem",
              color: MM.onSurfaceVariant,
              fontFamily: MM.fontBody,
            }}
          >
            {t("settings_no_results_hint", "Try another keyword or clear the search field.")}
          </Typography>
        </Card>
      )}
    </Box>
  );
});

export default SettingsPage;
